
import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Textarea } from './ui/Input';
import { Select } from './ui/Select';
import { Project, UpdateObraData, UserRole } from '../types';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '../hooks/useAuth';
import { useCompanies } from '../hooks/useCompanies';

interface EditObraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateObraData) => void;
  project: Project;
}

export const EditObraModal: React.FC<EditObraModalProps> = ({ isOpen, onClose, onSubmit, project }) => {
  const { users } = useUsers();
  const { user } = useAuth();
  const { companies } = useCompanies();

  const [formData, setFormData] = useState<UpdateObraData>({
    personInCharge: '',
    serviceDescription: '',
    companyId: '',
  });

  const [employeeOptions, setEmployeeOptions] = useState<{ value: string; label: string }[]>([]);
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    // Populate employee dropdown from context
    const empOptions = users
      .filter(u => u.role === UserRole.EMPLOYEE)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      .map(user => ({
        value: user.name,
        label: user.name,
      }));
    setEmployeeOptions(empOptions);
  }, [users]);

  useEffect(() => {
    if (user?.role === UserRole.DEV) {
      const sortedCompanies = [...companies].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      const options = sortedCompanies.map(company => ({
        value: company.id,
        label: `${company.name} - ${company.address.city}/${company.address.state}`,
      }));
      setCompanyOptions(options);
    }
  }, [companies, user]);

  useEffect(() => {
    if (project) {
      setFormData({
        personInCharge: project.personInCharge || '',
        serviceDescription: project.serviceDescription || '',
        companyId: project.companyId || '',
      });
    }
  }, [project, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.personInCharge || !formData.serviceDescription) {
      alert('Ambos os campos são obrigatórios.');
      return;
    }
    if (user?.role === UserRole.DEV && !formData.companyId) {
      alert('O campo Empresa é obrigatório para desenvolvedores.');
      return;
    }
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Editar Obra: ${project.name}`} size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        {user?.role === UserRole.DEV && (
          <Select
            label="Empresa* (Visível para DEV)"
            id="companyId"
            name="companyId"
            value={formData.companyId || ''}
            onChange={handleChange}
            options={companyOptions}
            placeholderOption="Selecione uma empresa"
            required
          />
        )}
        <Select
          label="Encarregado*"
          id="personInCharge"
          name="personInCharge"
          value={formData.personInCharge}
          onChange={handleChange}
          options={employeeOptions}
          placeholderOption="Selecione um encarregado"
          required
        />
        <Textarea
          label="Descrição do Serviço*"
          id="serviceDescription"
          name="serviceDescription"
          value={formData.serviceDescription}
          onChange={handleChange}
          rows={5}
          required
        />
        <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary">
            Salvar Alterações
          </Button>
        </div>
      </form>
    </Modal>
  );
};
